import { Icon } from "@mdi/react";
import { memo } from "react";

import { mdiClose, mdiDomain } from "@mdi/js";
import { getFilesFromEvent } from "utils/upload/getFilesFromEvent";

import { IconSpinner, OrgForecastFolderDashed } from "components/icons";

import OrganizationFolderNodeActions from "./OrganizationFolderNodeActions";
import OrganizationForecastNodeActions from "./OrganizationForecastNodeActions";
import {
  EditInput,
  TreeNodeContent,
  TreeNodeTitle
} from "./OrganizationForecasts.styles";

interface OrganizationTreeNodeProps {
  node;
  canEdit: boolean;
  editingNodeKey: string | null;
  editingNodeValue: string;
  highlightedFolderIdForDragDrop: string | null;
  wizardUploadLoading: boolean;
  deletingNodeKey: string | null;
  setEditingNodeValue: (value: string) => void;
  setHighlightedFolderForDragDrop: (folderId: string | null) => void;
  setHighlightedFolderDebounced: ((folderId: string) => void) & { cancel: () => void };
  handleSaveEdit: (nodeKey: string, value: string) => void;
  handleCancelEdit: () => void;
  handleEditFolder: (node) => void;
  handleDeleteFolder: (node) => void;
  handleDeleteForecast: (node) => void;
  uploadToOrganizationFolder: (
    folderName: string,
    folderId: string,
    files: File[]
  ) => Promise<void>;
}

const OrganizationTreeNode = memo(
  ({
    node,
    canEdit,
    editingNodeKey,
    editingNodeValue,
    highlightedFolderIdForDragDrop,
    wizardUploadLoading,
    deletingNodeKey,
    setEditingNodeValue,
    setHighlightedFolderForDragDrop,
    setHighlightedFolderDebounced,
    handleSaveEdit,
    handleCancelEdit,
    handleEditFolder,
    handleDeleteFolder,
    handleDeleteForecast,
    uploadToOrganizationFolder
  }: OrganizationTreeNodeProps) => {
    const isFolder = !node.isLeaf;
    const nodeKey = node.tempKey || node.key;
    const isEditing = editingNodeKey === nodeKey;
    const isBeingDeleted = deletingNodeKey === nodeKey;
    const isTemporaryFolder = node.tempKey && node.key === null;

    const dragDropClass =
      canEdit &&
      highlightedFolderIdForDragDrop === node.key &&
      isFolder &&
      !isEditing &&
      node.key !== null &&
      highlightedFolderIdForDragDrop !== null
        ? " drag-drop"
        : "";

    if (isFolder) {
      return (
        <TreeNodeTitle
          className={`tree-title${dragDropClass}`}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            if (
              node.key !== null &&
              e.dataTransfer &&
              e.dataTransfer.items &&
              e.dataTransfer.items.length > 0
            ) {
              setHighlightedFolderDebounced(node.key);
            }
          }}
          onDrop={async (e) => {
            e.preventDefault();
            const files = getFilesFromEvent(e);
            if (
              node.key !== null &&
              e.dataTransfer &&
              e.dataTransfer.items &&
              e.dataTransfer.items.length > 0 &&
              files.length > 0
            ) {
              await uploadToOrganizationFolder(node.title, node.key, files);
            }
            setHighlightedFolderForDragDrop(null);
          }}
          onDragLeave={(e) => {
            const targetBeingLeft = e.currentTarget;
            const targetBeingEntered = e.relatedTarget;
            if (!targetBeingLeft.contains(targetBeingEntered)) {
              setHighlightedFolderForDragDrop(null);
              setHighlightedFolderDebounced.cancel();
            }
          }}>
          {" "}
          <TreeNodeContent>
            {(node.children?.length ?? 0) === 0 ? (
              <OrgForecastFolderDashed />
            ) : (
              <Icon path={mdiDomain} size={1} />
            )}
            {isEditing ? (
              <EditInput
                value={editingNodeValue}
                onChange={(e) => setEditingNodeValue(e.target.value)}
                onPressEnter={() => handleSaveEdit(nodeKey, editingNodeValue)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    handleCancelEdit();
                  }
                }}
                autoFocus
                size="small"
              />
            ) : (
              <>
                <span className="title-text">{node.title}</span>
                {node.reserveCategory && (
                  <span className="title-reservecategory">{node.reserveCategory}</span>
                )}
              </>
            )}
          </TreeNodeContent>
          {canEdit && !isEditing && !isTemporaryFolder && (
            <OrganizationFolderNodeActions
              folderNode={node}
              onEdit={handleEditFolder}
              onDelete={handleDeleteFolder}
              canEdit={canEdit}
            />
          )}
          {canEdit && isEditing && isTemporaryFolder && (
            <button
              type="button"
              onClick={() => handleCancelEdit()}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                color: "#666",
                transition: "color 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#ff4d4f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#666";
              }}
              title="Remove folder">
              <Icon path={mdiClose} size={0.7} />
            </button>
          )}{" "}
          {canEdit &&
            ((highlightedFolderIdForDragDrop === node.key && node.key !== null) ||
              (isBeingDeleted && !isTemporaryFolder)) && (
              <div className="msg">
                {(wizardUploadLoading && highlightedFolderIdForDragDrop === node.key) ||
                (isBeingDeleted && !isTemporaryFolder) ? (
                  <IconSpinner size={12} />
                ) : null}
              </div>
            )}
        </TreeNodeTitle>
      );
    } else {
      return (
        <TreeNodeTitle>
          <TreeNodeContent>
            <span className="title-text">{node.title}</span>
          </TreeNodeContent>
          {canEdit && (
            <OrganizationForecastNodeActions
              forecastNode={node}
              onDelete={handleDeleteForecast}
              canEdit={canEdit}
            />
          )}
          {canEdit && isBeingDeleted && (
            <div className="msg">
              <IconSpinner size={14} padding={2} />
            </div>
          )}
        </TreeNodeTitle>
      );
    }
  }
);

OrganizationTreeNode.displayName = "OrganizationTreeNode";

export default OrganizationTreeNode;
